/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { FC, MutableRefObject, useState } from "react";

import cn from "classnames";
import { Controller, useForm, useWatch } from "react-hook-form";

import { useAuth } from "state";

import { GoBackIcon } from "components/icons";
import QuestionMarkIcon from "components/icons/QuestionMarkIcon";

import Tooltip from "components/ui/NewTooltip/Tooltip";
import { LoginResponse } from "pages/Login/types/login.types";

import styles from "../../style.module.css";
import Stack from "components/ui/Stack";
import Text from "components/ui/Text";
import { Input } from "components/ui/Input";
import { Button } from "components/ui/Button";
import { useEnvs } from "state/EnvironmentsContext/EnvContext";

interface BackupCodeAuthProps {
  setCurrentForm: (formName: string) => void;
  loginDataRef: MutableRefObject<LoginResponse | null>;
}

const BackupCodeAuth: FC<BackupCodeAuthProps> = ({
  setCurrentForm,
  loginDataRef,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { control } = useForm();

  const { handleLogin, handleLogout } = useAuth();
  const { isDevelopment, isStaging } = useEnvs((state) => state);

  const verificationCode = useWatch({
    control,
    name: "verificationCode",
    defaultValue: "",
  });

  const handleBackButton = () => {
    setIsLoading(true);
    handleLogout();
  };

  const isDisabled = isLoading || !verificationCode;
  return (
    <>
      <form className="flex flex-col w-full max-w-[545px] items-center">
        <Stack className="space-y-10">
          <Stack className="space-y-8">
            <Stack className="gap-1">
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleBackButton}>
                  <GoBackIcon fill="#fff" width="20" height="20" />
                </button>
                <h3 className="text-white text-lg md:text-xl font-bold">
                  Backup Code Authentication
                </h3>
              </div>

              <Text className="text-sm text-[#9A9A9A]">
                Enter one of your backup codes generated during the setup of
                2FA.
              </Text>
            </Stack>

            <Controller
              name="verificationCode"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  placeholder="59dc4b94d7"
                  maxLength={10}
                  onChange={(e) => {
                    const value = e.target.value;
                    const filteredValue = value.replace(/[^a-zA-Z0-9]/g, "");
                    field.onChange(filteredValue);
                  }}
                />
              )}
            />
          </Stack>

          <div className="flex flex-col items-center space-y-5">
            <Button
              variant="primary"
              type="submit"
              size="xl"
              shouldPreventDefault={true}
              isLoading={isLoading}
              className={cn({
                [styles.disabled]: isDisabled,
              })}
              disabled={isDisabled}
            >
              Verify
            </Button>

            <div className="flex items-center space-x-2">
              <Button
                onClick={() => setCurrentForm("otp_login")}
                variant="unstyled"
                className="text-xs font-bold text-white underline"
              >
                Switch to 2FA code
              </Button>

              <Tooltip
                customIcon={<QuestionMarkIcon />}
                tooltipClassName="w-80 !p-3 !bg-[#3C3C3C]"
                arrowColor="#3C3C3C"
              >
                <p className="text-xs text-white">
                  Switch back to using your authenticator app for two-factor
                  authentication. This option is available if you now have
                  access to your authenticator app and prefer to use it for
                  logging in.
                </p>
              </Tooltip>
            </div>
          </div>
        </Stack>
      </form>

      {(isDevelopment || isStaging) && (
        <Button
          variant="ghost"
          className="w-max p-2 rounded-md text-lg font-bold text-white mt-5"
          onClick={() => handleLogin(loginDataRef.current, "admin")}
        >
          Skip 2FA
        </Button>
      )}
    </>
  );
};

export default BackupCodeAuth;
